<script setup lang="ts">
import type { ITableFilterProps } from '@modules/tables/components/VTableFilter/types';
import type { ITableColumnsOptions } from '@modules/tables/types';
import VIconButton from '@components/buttons/VIconButton/VIconButton.vue';
import VCard from '@components/card/VCard/VCard.vue';
import {
  BALANCES_PERMISSIONS,
  TRANSLATION_GROUP_BALANCES,
} from '@modules/balances/constants';
import { useDownloadBalancesReportMutation } from '@modules/balances/mutations';
import { useBalancesListQuery } from '@modules/balances/queries';
import { ADMIN_BALANCES_EDIT_ROUTE_NAME } from '@modules/balances/submodules/edit/constants';
import { ADMIN_BALANCES_WITHDRAWAL_ROUTE_NAME } from '@modules/balances/submodules/withdrawal/constants';
import VDownloadReportButton from '@modules/download-report/components/VDownloadReportButton/VDownloadReportButton.vue';
import { useDownloadReport } from '@modules/download-report/composables';
import { ESmartFilterColumnType } from '@modules/smart-filters/enums';
import VTable from '@modules/tables/components/VTable/VTable.vue';
import { useTableData } from '@modules/tables/composables';

const {
  tableState,
  isLoading,
  isFetching,
  data,
  updateTableState,
  refreshTableData,
} = useTableData(useBalancesListQuery);

const {
  isReportLoading,
  onDownloadReport,
} = useDownloadReport(useDownloadBalancesReportMutation(), tableState);

const tableFilterOptions: ITableFilterProps = {
  pathForFilter: 'api2/admin/affiliate_cashouts/balance',
  actionForFilter: 'list',
  filterName: 'balance',
};

const columnOptions: ITableColumnsOptions = {
  merchant_name: {
    type: ESmartFilterColumnType.CHECKBOX,
  },
  id: {
    type: ESmartFilterColumnType.COMPARE,
  },
  email: {
    type: ESmartFilterColumnType.CHECKBOX,
    defaultMobilePreview: true,
  },
  username: {
    type: ESmartFilterColumnType.CHECKBOX,
  },
  amount: {
    type: ESmartFilterColumnType.COMPARE,
  },
  payment_system_name: {
    type: ESmartFilterColumnType.CHECKBOX,
  },
  payment_system_details: {
    type: ESmartFilterColumnType.CHECKBOX,
  },
};
</script>

<template>
  <VCard v-data-qa-auto-id="'BalancesListPage'" type="table">
    <VTable
      :data="data"
      :state="tableState"
      :loading="isLoading"
      :is-fetching="isFetching"
      :title-t-path="$getTPath(TRANSLATION_GROUP_BALANCES, 'HEADER')"
      :filter-props="tableFilterOptions"
      :columns-options="columnOptions"
      @state-update="updateTableState"
      @refresh="refreshTableData"
    >
      <template #recordAction="{ listItem }">
        <VIconButton
          v-if="$hasPermission(BALANCES_PERMISSIONS.EDIT)"
          icon="pi-file-edit"
          :to="{
            name: ADMIN_BALANCES_EDIT_ROUTE_NAME,
            params: {
              id: listItem.id,
            },
          }"
        />
        <VIconButton
          v-if="$hasPermission(BALANCES_PERMISSIONS.EDIT)"
          icon="pi-dollar"
          :to="{
            name: ADMIN_BALANCES_WITHDRAWAL_ROUTE_NAME,
            params: {
              id: listItem.id,
            },
          }"
        />
      </template>
      <template
        v-if="$hasPermission(BALANCES_PERMISSIONS.DOWNLOAD_LIST)"
        #footerButtons
      >
        <VDownloadReportButton
          :label="$getTranslation(TRANSLATION_GROUP_BALANCES, 'DOWNLOAD_REPORT')"
          :loading="isReportLoading"
          @download="onDownloadReport"
        />
      </template>
    </VTable>
  </VCard>
</template>
